/**
 * RefundRulesCard
 * ---------------
 * Carte premium explicitant les règles de remboursement & de report
 * pour les séances de coaching humain. Conforme à la charte Coach DIA
 * (gradient violet profond → vivid, accent doré, border subtil).
 *
 * Usage :
 *   <RefundRulesCard variant="compact" />  // 1 seule ligne inline
 *   <RefundRulesCard />                    // pleine carte (défaut)
 */
import React, { useState } from 'react';
import { Shield, Clock, CalendarClock, CheckCircle2, XCircle, ChevronDown } from 'lucide-react';

export default function RefundRulesCard({ variant = 'full', className = '' }) {
  const [open, setOpen] = useState(false);

  if (variant === 'compact') {
    return (
      <div
        data-testid="refund-rules-compact"
        className={`flex items-center gap-2 text-xs text-[#4B3869] bg-white border border-violet-100 rounded-full px-3 py-1.5 ${className}`}
      >
        <Shield className="w-3.5 h-3.5 text-[#6D28D9]" />
        <span>
          Annulation gratuite &gt; 24 h · Report gratuit (1 max) · Sans remboursement &lt; 24 h
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="refund-rules-card"
      className={`bg-gradient-to-br from-white via-[#FBEAFF]/40 to-[#F3EEFF]/60 border border-violet-100 rounded-2xl p-5 shadow-sm ${className}`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start gap-3 text-left group"
        data-testid="refund-rules-toggle"
      >
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1C1147] to-[#6D28D9] flex items-center justify-center flex-shrink-0 shadow-sm">
          <Shield className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1">
          <p className="text-[11px] uppercase tracking-wider text-[#6D28D9] font-bold mb-0.5">
            Règles d'annulation & de report
          </p>
          <p className="text-sm font-semibold text-[#1C1147]">
            Transparent et sans surprise — cliquez pour voir le détail
          </p>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-[#6D28D9] flex-shrink-0 transition-transform mt-2 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 animate-in fade-in slide-in-from-top-2">
          <Rule
            icon={CheckCircle2}
            iconBg="bg-emerald-50"
            iconColor="text-emerald-600"
            title="Annulation > 24 h"
            body="Remboursement intégral automatique sur votre moyen de paiement d'origine (5 à 10 jours ouvrés)."
          />
          <Rule
            icon={XCircle}
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
            title="Annulation < 24 h"
            body="Aucun remboursement applicable, conformément à nos conditions (délai glissant Europe/Paris)."
          />
          <Rule
            icon={CalendarClock}
            iconBg="bg-violet-50"
            iconColor="text-[#6D28D9]"
            title="Report par l'utilisateur"
            body="Gratuit, limité à 1 report par séance. Sélectionnez un nouveau créneau disponible du coach."
          />
          <Rule
            icon={Clock}
            iconBg="bg-violet-50"
            iconColor="text-[#6D28D9]"
            title="Report demandé par le coach"
            body="Vous recevez un email avec lien d'approbation. Libre à vous d'accepter ou de refuser."
          />
          <Rule
            icon={Shield}
            iconBg="bg-[#FEF3C7]"
            iconColor="text-[#B45309]"
            title="Annulation par le coach"
            body="Remboursement intégral automatique. Vous êtes notifié(e) et pouvez immédiatement réserver une nouvelle séance."
            fullWidth
          />
        </div>
      )}
    </div>
  );
}

function Rule({ icon: Icon, iconBg, iconColor, title, body, fullWidth }) {
  return (
    <div
      className={`bg-white border border-gray-100 rounded-xl p-3.5 flex gap-3 ${
        fullWidth ? 'sm:col-span-2' : ''
      }`}
    >
      <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-4 h-4 ${iconColor}`} />
      </div>
      <div>
        <p className="text-sm font-bold text-[#1C1147] mb-0.5">{title}</p>
        <p className="text-xs text-[#4B3869] leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
